import { useState, useEffect } from 'react';
import { Tag, Coins, Users, TrendingUp } from 'lucide-react';
import { API_URL } from '../../config/api';

const CampaignPerformance = ({ getDateRange, formatCurrency }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch_ = async () => {
      setLoading(true);
      try {
        const dr = getDateRange();
        const params = `start_date=${dr.start}&end_date=${dr.end}`;
        const res = await fetch(`${API_URL}/reports/campaign-performance?${params}`);
        if (res.ok) setData(await res.json());
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    fetch_();
  }, [getDateRange]);

  if (loading) return <div className="empty-state"><div className="empty-icon">⏳</div><p>Kampanya verileri yükleniyor...</p></div>;

  if (!data.length) return (
    <div className="tab-content">
      <div className="empty-state">
        <div className="empty-icon">🎯</div>
        <p>Bu dönemde kampanya kullanımı bulunmuyor.</p>
      </div>
    </div>
  );

  const totalUsage = data.reduce((s, c) => s + (c.usage_count || 0), 0);
  const totalDiscount = data.reduce((s, c) => s + parseFloat(c.total_discount || 0), 0);
  const totalRevenue = data.reduce((s, c) => s + parseFloat(c.total_final_revenue || 0), 0);
  const totalOriginal = data.reduce((s, c) => s + parseFloat(c.total_original || 0), 0);
  const uniqueCustomers = data.reduce((s, c) => s + (c.unique_customers || 0), 0);
  const maxUsage = Math.max(...data.map(c => c.usage_count), 1);

  const campaignTypes = {
    percentage_discount: 'Yüzde İndirim',
    fixed_discount: 'Sabit İndirim',
    buy_x_get_y: 'X Al Y Öde',
    birthday: 'Doğum Günü',
    loyalty: 'Sadakat',
    seasonal: 'Mevsimsel',
  };

  return (
    <div className="tab-content">
      <div className="stats-grid">
        <div className="stat-card primary">
          <div className="stat-icon"><Tag size={20} /></div>
          <div className="stat-info">
            <div className="stat-value">{totalUsage}</div>
            <div className="stat-label">Toplam Kullanım</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon"><Coins size={20} style={{ color: '#e74c3c' }} /></div>
          <div className="stat-info">
            <div className="stat-value">{formatCurrency(totalDiscount)}</div>
            <div className="stat-label">Toplam İndirim</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon"><Coins size={20} style={{ color: '#0f766e' }} /></div>
          <div className="stat-info">
            <div className="stat-value">{formatCurrency(totalRevenue)}</div>
            <div className="stat-label">Kampanyalı Gelir</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon"><Users size={20} style={{ color: '#0f766e' }} /></div>
          <div className="stat-info">
            <div className="stat-value">{uniqueCustomers}</div>
            <div className="stat-label">Etkilenen Müşteri</div>
          </div>
        </div>
      </div>

      {/* İndirim vs Gelir Özeti */}
      {totalOriginal > 0 && (
        <div className="chart-card">
          <h3>İndirim Etkisi</h3>
          <div style={{ display: 'flex', gap: 24, alignItems: 'center', marginTop: 12 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 12, color: '#5a6c7d', marginBottom: 4 }}>Orijinal Toplam</div>
              <div style={{ fontSize: 20, fontWeight: 700 }}>{formatCurrency(totalOriginal)}</div>
            </div>
            <div style={{ fontSize: 24, color: '#e74c3c' }}>→</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 12, color: '#5a6c7d', marginBottom: 4 }}>İndirim Sonrası</div>
              <div style={{ fontSize: 20, fontWeight: 700, color: '#0f766e' }}>{formatCurrency(totalRevenue)}</div>
            </div>
            <div style={{ flex: 1, textAlign: 'center' }}>
              <div style={{ fontSize: 12, color: '#5a6c7d', marginBottom: 4 }}>İndirim Oranı</div>
              <div style={{ fontSize: 20, fontWeight: 700, color: '#e74c3c' }}>
                %{(totalDiscount / totalOriginal * 100).toFixed(1)}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Kampanya Bar Chart */}
      <div className="chart-card">
        <h3>Kampanya Bazlı Kullanım</h3>
        <div className="horizontal-bars">
          {data.map((c, i) => (
            <div key={c.campaign_id} className="h-bar-item">
              <div className="h-bar-label">
                <span className="h-bar-name">{c.campaign_name}</span>
                <span className="h-bar-value">{c.usage_count} kullanım</span>
              </div>
              <div className="h-bar-track">
                <div className={`h-bar-fill color-${(i % 5) + 1}`} style={{ width: `${(c.usage_count / maxUsage) * 100}%` }} />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Detay Tablosu */}
      <div className="data-card">
        <h3>Kampanya Detayları</h3>
        <div className="table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th>Kampanya</th>
                <th>Tür</th>
                <th>Kullanım</th>
                <th>Tekil Müşteri</th>
                <th>Orijinal Toplam</th>
                <th>Toplam İndirim</th>
                <th>Son Gelir</th>
                <th>Ort. İndirim</th>
              </tr>
            </thead>
            <tbody>
              {data.map(c => (
                <tr key={c.campaign_id}>
                  <td><strong>{c.campaign_name}</strong></td>
                  <td>
                    <span className="days-badge" style={{ background: '#e0f2fe', color: '#0369a1' }}>
                      {campaignTypes[c.campaign_type] || c.campaign_type || '-'}
                    </span>
                  </td>
                  <td><strong>{c.usage_count}</strong></td>
                  <td>{c.unique_customers}</td>
                  <td>{formatCurrency(c.total_original)}</td>
                  <td style={{ color: '#e74c3c', fontWeight: 600 }}>{formatCurrency(c.total_discount)}</td>
                  <td style={{ color: '#0f766e', fontWeight: 600 }}>{formatCurrency(c.total_final_revenue)}</td>
                  <td>{formatCurrency(c.avg_discount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default CampaignPerformance;
